'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, useReducedMotion } from 'motion/react';
import {
  Flame, Phone, Headphones, LayoutGrid, UtensilsCrossed, Calendar, Percent,
  Users, ArrowRight, Check, Shield, Globe, Mic, ChefHat, ExternalLink,
  LogIn, Menu, X
} from 'lucide-react';

interface LandingTenant {
  slug: string;
  name: string;
  tagline: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  city: string;
  phone: string;
  landing_order: number;
}

const DEFAULT_BASE_DOMAIN = process.env.NEXT_PUBLIC_APP_BASE_DOMAIN?.trim() || 'okai.pt';

function browserBaseDomain() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname.endsWith('.localhost')
    ? 'localhost'
    : hostname.split('.').slice(-2).join('.');
}

const FEATURES = [
  {
    icon: Headphones,
    title: 'Rececionista por Voz IA',
    desc: 'Atende chamadas 24/7 com um agente versionado pelo restaurante: prompt, modelos, voz e ferramentas ficam isolados por tenant.',
    accent: 'bg-violet-600',
  },
  {
    icon: LayoutGrid,
    title: 'Multi-tenant nativo',
    desc: 'Cada restaurante tem o seu subdominio (virabrasa.okai.pt), branding proprio e dados isolados. Super-admin gere tudo do apex.',
    accent: 'bg-blue-600',
  },
  {
    icon: UtensilsCrossed,
    title: 'Ementa digital',
    desc: 'Cardapio online com categorias, itens, tags e disponibilidade. O cliente pede direto da pagina publica.',
    accent: 'bg-amber-600',
  },
  {
    icon: Users,
    title: 'Sala e mesas',
    desc: 'Mapa visual da sala com mesas arrastaveis, estados (livre/ocupada/limpeza) e associacao de pedidos.',
    accent: 'bg-emerald-600',
  },
  {
    icon: Calendar,
    title: 'Eventos e rodizios',
    desc: 'Calendario de rodizios, buffets e eventos privados com reservas, capacidade e selecao de carnes.',
    accent: 'bg-rose-600',
  },
  {
    icon: Percent,
    title: 'Promocoes sazonais',
    desc: 'Campanhas com codigos, descontos, limites de uso e banners em destaque na app cliente.',
    accent: 'bg-orange-600',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Configura o teu tenant',
    desc: 'O super-admin cria o teu restaurante, atribui o subdominio e o numero de voz. Recebes contas admin + funcionario.',
  },
  {
    n: '02',
    title: 'Personaliza a ementa',
    desc: 'Sobe o cardapio, define horarios e configura promocoes. A app cliente fica online no teu subdominio.',
  },
  {
    n: '03',
    title: 'Atende por voz e online',
    desc: 'O agente IA atende as chamadas, regista reservas e os clientes pedem pela app. Gere tudo no painel /admin.',
  },
];

export default function PlatformLanding() {
  const reduce = useReducedMotion();
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tenants, setTenants] = useState<LandingTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [baseDomain, setBaseDomain] = useState(DEFAULT_BASE_DOMAIN);

  useEffect(() => {
    setBaseDomain(browserBaseDomain());
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const tenantUrl = (slug: string) => {
    if (typeof window === 'undefined') return `https://${slug}.${baseDomain}`;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${window.location.protocol}//${slug}.${baseDomain}${port}`;
  };

  useEffect(() => {
    fetch('/api/public/tenants')
      .then((r) => r.json())
      .then((data) => setTenants(data.data ?? []))
      .catch(() => setTenants([]))
      .finally(() => setTenantsLoading(false));
  }, []);

  const fadeUp = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] as const },
  });

  return (
    <div className="min-h-screen bg-surface-1 text-ink-900">
      {/* ===== Nav ===== */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-surface-0/90 backdrop-blur-md border-b border-surface-3 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center shadow-sm">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Okai</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7 text-sm text-ink-600">
            <a href="#features" className="hover:text-ink-900 transition-colors">Funcionalidades</a>
            <a href="#tenants" className="hover:text-ink-900 transition-colors">Restaurantes</a>
            <a href="#voz" className="hover:text-ink-900 transition-colors">Voz IA</a>
            <a href="#como-funciona" className="hover:text-ink-900 transition-colors">Como funciona</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a href="/auth" className="btn btn-secondary btn-sm">
              <LogIn className="w-4 h-4" /> Entrar
            </a>
            <a href="/super-admin/auth" className="btn btn-sm bg-ink-900 text-white hover:bg-ink-800">
              <Shield className="w-4 h-4" /> Super Admin
            </a>
          </div>

          <button
            onClick={() => setNavOpen(!navOpen)}
            className="md:hidden min-h-11 min-w-11 p-2 rounded-lg hover:bg-surface-2"
            aria-label={navOpen ? 'Fechar navegação' : 'Abrir navegação'}
            aria-expanded={navOpen}
          >
            {navOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {navOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-surface-0 border-t border-surface-3 px-4 py-4 space-y-3"
          >
            <a href="#features" onClick={() => setNavOpen(false)} className="block py-2 text-ink-700">Funcionalidades</a>
            <a href="#tenants" onClick={() => setNavOpen(false)} className="block py-2 text-ink-700">Restaurantes</a>
            <a href="#voz" onClick={() => setNavOpen(false)} className="block py-2 text-ink-700">Voz IA</a>
            <a href="#como-funciona" onClick={() => setNavOpen(false)} className="block py-2 text-ink-700">Como funciona</a>
            <a href="/auth" className="btn btn-secondary w-full">Entrar</a>
            <a href="/super-admin/auth" className="btn bg-ink-900 text-white w-full">Super Admin</a>
          </motion.div>
        )}
      </header>

      {/* ===== Hero ===== */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden bg-brand-50 border-b border-brand/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <motion.h1
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-bold text-4xl sm:text-6xl lg:text-7xl tracking-[-0.035em] leading-[1.05] mb-6"
          >
            O restaurante que
            <br />
            <span className="text-brand">
              atende, vende e gere
            </span>
            <br />
            sozinho.
          </motion.h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="text-lg sm:text-xl text-ink-500 max-w-2xl mx-auto mb-10"
          >
            Rececionista por voz IA, ementa digital, sala de mesas, eventos e promocoes.
            Cada restaurante no seu subdominio, com branding proprio e gestao centralizada.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a href="#tenants" className="btn-gradient !w-auto px-7 py-3.5 text-base flex items-center gap-2">
              Ver restaurantes <ArrowRight className="w-4 h-4" />
            </a>
            <a href="/auth" className="btn btn-secondary px-7 py-3.5 text-base flex items-center gap-2">
              <LogIn className="w-4 h-4" /> Entrar no painel
            </a>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto mt-16"
          >
            {[
              { v: '5', l: 'Restaurantes ativos' },
              { v: '24/7', l: 'Atendimento por voz' },
              { v: '100%', l: 'Multi-tenant' },
            ].map((s) => (
              <div key={s.l} className="text-center">
                <div className="font-display font-bold text-3xl sm:text-4xl text-brand">
                  {s.v}
                </div>
                <div className="text-xs sm:text-sm text-ink-500 mt-1">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="features" className="py-20 sm:py-28 bg-surface-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-4">
              Tudo numa so plataforma
            </h2>
            <p className="text-lg text-ink-500 max-w-2xl mx-auto">
              Do atendimento telefonico ao pedido online, passando pela gestao da sala e promocoes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                {...fadeUp(i * 0.08)}
                className="fk-card p-6 group"
              >
                <div className={`w-12 h-12 rounded-xl ${f.accent} flex items-center justify-center mb-4 shadow-sm group-hover:scale-105 transition-transform`}>
                  <f.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 mb-2">{f.title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Tenants showcase ===== */}
      <section id="tenants" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-4">
              {tenants.length > 0 ? `${tenants.length} restaurantes, ${tenants.length} subdominios` : 'Restaurantes na plataforma'}
            </h2>
            <p className="text-lg text-ink-500 max-w-2xl mx-auto">
              Cada restaurante tem a sua propria pagina publica com branding, ementa e pedidos online.
            </p>
          </motion.div>

          {tenantsLoading ? (
            <div className="text-center py-12 text-ink-400">
              <Flame className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>A carregar restaurantes...</p>
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-12 text-ink-500">
              <Flame className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium text-ink-700">Sem restaurantes disponíveis</p>
              <p className="mt-1 text-sm">Tenta novamente dentro de alguns instantes.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tenants.map((t, i) => (
              <motion.a
                key={t.slug}
                href={tenantUrl(t.slug)}
                {...fadeUp(i * 0.08)}
                className="fk-card overflow-hidden group block"
              >
                <div
                  className="p-6 relative h-32 flex items-end"
                  style={{ background: `linear-gradient(135deg, ${t.primaryColor}, ${t.secondaryColor})` }}
                >
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Flame className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl text-white">{t.name}</h3>
                    <p className="text-white/80 text-sm">{t.tagline}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-ink-500 font-mono mb-3">
                    <Globe className="w-3.5 h-3.5" /> {t.slug}.{baseDomain}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ink-600">{t.city}</span>
                    <span className="flex items-center gap-1 text-brand text-sm font-medium group-hover:gap-2 transition-all">
                      Ver ementa <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}

            {/* Super-admin card */}
            <motion.a
              href="/super-admin/auth"
              {...fadeUp(tenants.length * 0.08)}
              className="fk-card overflow-hidden group block border-2 border-ink-900/10"
            >
              <div className="p-6 h-32 flex items-end bg-ink-900">
                <div className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-white">Super Admin</h3>
                  <p className="text-white/70 text-sm">Gestao da plataforma</p>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-ink-500 font-mono mb-3">
                  <Globe className="w-3.5 h-3.5" /> {baseDomain}/super-admin
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-600">Apex domain</span>
                  <span className="flex items-center gap-1 text-ink-900 text-sm font-medium group-hover:gap-2 transition-all">
                    Gerir <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </motion.a>
          </div>
          )}
        </div>
      </section>

      {/* ===== Voice IA highlight ===== */}
      <section id="voz" className="py-20 sm:py-28 bg-ink-900 text-white relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp()}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/80 text-sm mb-5">
                <Mic className="w-3.5 h-3.5" /> Voz IA
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-5">
                A rececionista que nunca dorme.
              </h2>
              <p className="text-lg text-white/70 mb-6 leading-relaxed">
                O agente IA atende as chamadas, entende o cliente, regista reservas e pedidos e
                despacha tudo para o painel. Cada restaurante publica o seu prompt, modelos, voz e
                ferramentas como uma release imutavel e isolada.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Atende 24/7 no numero do restaurante',
                  'Regista reservas de mesa por voz',
                  'Valida disponibilidade em tempo real',
                  'Handoff para humano quando necessario',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-white/90">
                    <Check className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="#como-funciona" className="btn-gradient !w-auto px-6 py-3 inline-flex items-center gap-2">
                Como funciona <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            <motion.div {...fadeUp(0.15)} className="relative">
              {/* Phone mockup */}
              <div className="relative mx-auto max-w-sm">
                <div className="bg-ink-800 rounded-[2.5rem] p-3 shadow-2xl border border-white/10">
                  <div className="bg-ink-950 rounded-[2rem] p-6 min-h-[480px] flex flex-col">
                    <div className="text-center mb-6">
                      <div className="text-xs text-white/40 mb-1">Chamada a decorrer</div>
                      <div className="font-mono text-brand text-lg">+351 308 806 090</div>
                    </div>

                    <div className="flex-1 space-y-4">
                      {/* Waveform */}
                      <div className="flex items-center justify-center gap-1 h-16">
                        {[...Array(20)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1 bg-brand rounded-full"
                            animate={reduce ? {} : { height: [8, 32 + Math.random() * 24, 8] }}
                            transition={{ duration: 0.8 + Math.random() * 0.4, repeat: Infinity, delay: i * 0.05 }}
                          />
                        ))}
                      </div>

                      <div className="bg-white/5 rounded-2xl p-4 text-sm text-white/80">
                        <div className="text-xs text-brand mb-1">Agente</div>
                        Bom dia, aqui e a Virabrasa. Para quantas pessoas e a reserva?
                      </div>

                      <div className="bg-brand/15 rounded-2xl p-4 text-sm text-white">
                        <div className="text-xs text-brand/70 mb-1">Cliente</div>
                        Para 4 pessoas, amanha as 20h.
                      </div>

                      <div className="bg-white/5 rounded-2xl p-4 text-sm text-white/80">
                        <div className="text-xs text-brand mb-1">Agente</div>
                        Reserva confirmada para 4 pessoas, amanha as 20h. Nome, por favor?
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-danger flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section id="como-funciona" className="py-20 sm:py-28 bg-surface-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-4">
              Como funciona
            </h2>
            <p className="text-lg text-ink-500">Do zero ao primeiro pedido em 3 passos.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp(i * 0.12)} className="relative">
                <div className="font-display font-bold text-6xl text-brand/20 mb-3">{s.n}</div>
                <h3 className="font-display font-bold text-xl text-ink-900 mb-2">{s.title}</h3>
                <p className="text-sm text-ink-500 leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 text-ink-200">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="py-20 sm:py-28 relative overflow-hidden bg-brand-50 border-t border-brand/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div {...fadeUp()}>
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand flex items-center justify-center shadow-sm">
              <ChefHat className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-4">
              Pronto para o teu restaurante?
            </h2>
            <p className="text-lg text-ink-500 mb-8">
              Entra no painel ou fala com o super-admin para configurar o teu tenant.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/auth" className="btn-gradient !w-auto px-7 py-3.5 text-base flex items-center gap-2">
                <LogIn className="w-4 h-4" /> Entrar no painel
              </a>
              <a href="#tenants" className="btn btn-secondary px-7 py-3.5 text-base">
                Ver restaurantes
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="bg-ink-900 text-white/60 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="font-display font-bold text-white">Okai</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/auth" className="hover:text-white transition-colors">Entrar</a>
              <a href="/super-admin/auth" className="hover:text-white transition-colors">Super Admin</a>
              <a href={`https://${baseDomain}`} className="hover:text-white transition-colors">{baseDomain}</a>
            </div>
          </div>
          <div className="text-center text-xs mt-6 text-white/30">
            Plataforma SaaS multi-tenant para restauração. Voz IA, ementa digital, sala e promocoes.
          </div>
        </div>
      </footer>
    </div>
  );
}
